import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";

type AppRole = "owner" | "agent" | "user";

interface ImpersonationState {
  active: boolean;
  logId: string | null;
  targetUserId: string | null;
  targetRole: AppRole | null;
  targetDisplayName: string | null;
  targetAvatarUrl: string | null;
  targetEmail: string | null;
  targetTrialExpiresAt: string | null;
  targetSubscriptionPlan: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  role: AppRole | null;
  displayName: string | null;
  avatarUrl: string | null;
  trialExpiresAt: string | null;
  subscriptionPlan: string | null;
  accountStatus: string | null;
  isTrialActive: boolean;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  // Impersonation
  impersonation: ImpersonationState;
  startImpersonation: (targetUserId: string) => Promise<void>;
  endImpersonation: () => Promise<void>;
  // Effective values (respects impersonation)
  effectiveRole: AppRole | null;
  effectiveDisplayName: string | null;
  effectiveAvatarUrl: string | null;
  effectiveUserId: string | null;
  effectiveTrialExpiresAt: string | null;
  effectiveSubscriptionPlan: string | null;
}

const defaultImpersonation: ImpersonationState = {
  active: false,
  logId: null,
  targetUserId: null,
  targetRole: null,
  targetDisplayName: null,
  targetAvatarUrl: null,
  targetEmail: null,
  targetTrialExpiresAt: null,
  targetSubscriptionPlan: null,
};

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  role: null,
  displayName: null,
  avatarUrl: null,
  trialExpiresAt: null,
  subscriptionPlan: null,
  accountStatus: null,
  isTrialActive: false,
  loading: true,
  signOut: async () => {},
  refreshProfile: async () => {},
  impersonation: defaultImpersonation,
  startImpersonation: async () => {},
  endImpersonation: async () => {},
  effectiveRole: null,
  effectiveDisplayName: null,
  effectiveAvatarUrl: null,
  effectiveUserId: null,
  effectiveTrialExpiresAt: null,
  effectiveSubscriptionPlan: null,
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [role, setRole] = useState<AppRole | null>(null);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [trialExpiresAt, setTrialExpiresAt] = useState<string | null>(null);
  const [subscriptionPlan, setSubscriptionPlan] = useState<string | null>(null);
  const [accountStatus, setAccountStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [impersonation, setImpersonation] = useState<ImpersonationState>(defaultImpersonation);

  const isTrialActive = trialExpiresAt ? new Date(trialExpiresAt) > new Date() : false;

  const fetchUserMeta = async (userId: string, retries = 3) => {
    try {
      const [{ data: roleData }, { data: profileData }] = await Promise.all([
        supabase.from("user_roles").select("role").eq("user_id", userId).maybeSingle(),
        supabase.from("profiles").select("display_name, avatar_url, trial_expires_at, subscription_plan, status").eq("user_id", userId).maybeSingle(),
      ]);
      
      if (!profileData && retries > 0) {
        await new Promise(r => setTimeout(r, 1000));
        return fetchUserMeta(userId, retries - 1);
      }
      
      setRole((roleData?.role as AppRole) || null);
      setDisplayName(profileData?.display_name || null);
      setAvatarUrl(profileData?.avatar_url || null);
      setTrialExpiresAt(profileData?.trial_expires_at || null);
      setSubscriptionPlan(profileData?.subscription_plan || null);
      setAccountStatus((profileData as any)?.status || null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let initialLoad = true;

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          if (!initialLoad) {
            setLoading(true);
            fetchUserMeta(session.user.id);
          }
        } else {
          setRole(null);
          setDisplayName(null);
          setAvatarUrl(null);
          setTrialExpiresAt(null);
          setSubscriptionPlan(null);
          setAccountStatus(null);
          setImpersonation(defaultImpersonation);
          setLoading(false);
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchUserMeta(session.user.id).then(() => {
          initialLoad = false;
        });
      } else {
        setLoading(false);
        initialLoad = false;
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const refreshProfile = async () => {
    if (user) await fetchUserMeta(user.id);
  };

  const startImpersonation = async (targetUserId: string) => {
    if (role !== "owner" && role !== "agent") return;
    try {
      const { data, error } = await supabase.functions.invoke("impersonate", {
        body: { action: "start", target_user_id: targetUserId },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      setImpersonation({
        active: true,
        logId: data.log_id,
        targetUserId,
        targetRole: data.target.role as AppRole,
        targetDisplayName: data.target.display_name,
        targetAvatarUrl: data.target.avatar_url,
        targetEmail: data.target.email,
        targetTrialExpiresAt: data.target.trial_expires_at,
        targetSubscriptionPlan: data.target.subscription_plan,
      });
    } catch (err: any) {
      throw err;
    }
  };

  const endImpersonation = async () => {
    try {
      if (impersonation.logId) {
        await supabase.functions.invoke("impersonate", {
          body: { action: "end", log_id: impersonation.logId },
        });
      }
    } catch {} finally {
      setImpersonation(defaultImpersonation);
    }
  };

  const signOut = async () => {
    if (impersonation.active) {
      await endImpersonation();
    }
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setRole(null);
    setDisplayName(null);
    setAvatarUrl(null);
    setTrialExpiresAt(null);
    setSubscriptionPlan(null);
    setAccountStatus(null);
  };

  // Effective values: when impersonating, show target's data; otherwise show own
  const effectiveRole = impersonation.active ? impersonation.targetRole : role;
  const effectiveDisplayName = impersonation.active ? impersonation.targetDisplayName : displayName;
  const effectiveAvatarUrl = impersonation.active ? impersonation.targetAvatarUrl : avatarUrl;
  const effectiveUserId = impersonation.active ? impersonation.targetUserId : user?.id || null;
  const effectiveTrialExpiresAt = impersonation.active ? impersonation.targetTrialExpiresAt : trialExpiresAt;
  const effectiveSubscriptionPlan = impersonation.active ? impersonation.targetSubscriptionPlan : subscriptionPlan;

  return (
    <AuthContext.Provider value={{
      user, session, role, displayName, avatarUrl, trialExpiresAt, subscriptionPlan, accountStatus, isTrialActive,
      loading, signOut, refreshProfile,
      impersonation, startImpersonation, endImpersonation,
      effectiveRole, effectiveDisplayName, effectiveAvatarUrl, effectiveUserId,
      effectiveTrialExpiresAt, effectiveSubscriptionPlan,
    }}>
      {children}
    </AuthContext.Provider>
  );
}
